import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	Crew,
	InformationRetrievalPickers,
	ProducedCombination,
	ProductSize,
	ProductType,
} from "../../information-retrieval/models/InformationRetrieval";
import type { StraightenerRecipeValueRow } from "../../../production/straightener-recipes/models/StraightenerRecipe";
import type {
	RecipeProposal,
	RecipeProposalRequest,
	RecipeProposalStat,
	RecipeProposalSummary,
	StraightenerInformationRetrievalFilters,
	StraightenerInformationRetrievalRow,
} from "../models/StraightenerInformationRetrieval";

/** Break values come from FLOAT(32) PLC samples; six decimals drop the noise. */
const roundFloat = (value: number | null): number | null =>
	value === null || value === undefined
		? null
		: Number.parseFloat(value.toFixed(6));

const FLOAT_COLUMNS = [
	"ActualSum",
	"ActualAvg",
	"ActualMin",
	"ActualMax",
	"ActualSumSquares",
	"TargetSum",
	"TargetAvg",
	"RecipeMinSum",
	"RecipeMinAvg",
	"RecipeMaxSum",
	"RecipeMaxAvg",
] as const;

const roundRow = (
	row: StraightenerInformationRetrievalRow,
): StraightenerInformationRetrievalRow => {
	const rounded = { ...row };
	for (const column of FLOAT_COLUMNS) {
		rounded[column] = roundFloat(row[column]);
	}
	return rounded;
};

export const getStraightenerInformationRetrievalData = async (
	filters: StraightenerInformationRetrievalFilters,
	signal?: AbortSignal,
): Promise<StraightenerInformationRetrievalRow[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "start", value: filters.start },
		{ name: "end", value: filters.end },
		{ name: "productTypeId", value: filters.productTypeId },
		{ name: "productSizeId", value: filters.productSizeId },
		// '0' means "all shifts" in the picker, which the procedure expects as
		// NULL; '-' it resolves itself to the crew of the latest run.
		{
			name: "shiftId",
			value: filters.shiftId === "0" ? null : filters.shiftId,
		},
		{ name: "groupByDate", value: filters.groupByDate },
		{ name: "groupByShift", value: filters.groupByShift },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetStraightenerInformationRetrievalDataWeb]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error fetching straightener information retrieval data",
		);
	}

	const rows = get(
		resp,
		"data.tables[0].rows",
		[],
	) as StraightenerInformationRetrievalRow[];

	return rows.map(roundRow);
};

const escapeXml = (s: string): string =>
	s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");

/**
 * A full straightener recipe proposed from the runs of the ticked groups: the
 * value most runs used per setpoint, plus how steady each one was.
 */
export const getStraightenerRecipeProposal = async (
	request: RecipeProposalRequest,
): Promise<RecipeProposal> => {
	const apiService = new MESApiService();

	const groupsXml = `<Groups>${request.groups
		.map(
			(g) =>
				`<G date="${escapeXml(g.date ?? "")}" shift="${escapeXml(g.shift ?? "")}"/>`,
		)
		.join("")}</Groups>`;

	const resp = await apiService.callV2(
		"[MILL].[GetStraightenerRecipeProposalFromRuns]",
		[
			{ name: "start", value: request.start },
			{ name: "end", value: request.end },
			{ name: "productSizeId", value: request.productSizeId },
			{ name: "shiftId", value: request.shiftId },
			{ name: "groups", value: groupsXml },
		],
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error building the recipe proposal");
	}

	return {
		values: get(
			resp,
			"data.tables[0].rows",
			[],
		) as StraightenerRecipeValueRow[],
		stats: get(resp, "data.tables[1].rows", []) as RecipeProposalStat[],
		summary: get(
			resp,
			"data.tables[2].rows[0]",
			null,
		) as RecipeProposalSummary | null,
	};
};

export interface StraightenerPickers extends InformationRetrievalPickers {
	/** Runs in the dates the PLC reported no product for. */
	runsWithoutProduct: number;
	/** Runs the straightener recorded in the dates, with or without history. */
	runsInDates: number;
	/** Of those, the runs the history holds. */
	trackedRunsInDates: number;
	/** First production date with history; null while the history is empty. */
	historyFrom: string | null;
}

/** The mill pickers' five result sets, read from the straightener runs, plus runs without product and history coverage. */
export const getStraightenerInformationRetrievalPickers = async (
	start: string,
	end: string,
	signal?: AbortSignal,
): Promise<StraightenerPickers> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[MILL].[GetStraightenerInformationRetrievalPickersDataWeb]",
		[
			{ name: "start", value: start },
			{ name: "end", value: end },
			{ name: "onlyActives", value: true },
		],
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching filters");
	}

	return {
		crews: get(resp, "data.tables[0].rows", []) as Crew[],
		productSizes: get(resp, "data.tables[1].rows", []) as ProductSize[],
		productTypes: get(resp, "data.tables[2].rows", []) as ProductType[],
		combinations: get(resp, "data.tables[3].rows", []) as ProducedCombination[],
		currentCrewId: get(resp, "data.tables[4].rows[0].CrewId", null) as
			| string
			| null,
		runsWithoutProduct: get(
			resp,
			"data.tables[5].rows[0].RunsWithoutProduct",
			0,
		) as number,
		runsInDates: get(resp, "data.tables[6].rows[0].RunsInDates", 0) as number,
		trackedRunsInDates: get(
			resp,
			"data.tables[6].rows[0].TrackedRunsInDates",
			0,
		) as number,
		historyFrom: get(resp, "data.tables[6].rows[0].HistoryFrom", null) as
			| string
			| null,
	};
};
